import Backbone from 'backbone';
import * as d3 from 'd3';

import {CRISPRAnalysis} from 'constants/CRISPRConstants';
import * as MapsConstants from 'constants/MapsConstants';
import {getDefaultAaSequenceLayout} from 'constants/MapsConstants';
import {AaSequenceColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import Type from 'molbio/maps/Type';
import {GRAY, ORANGE, RED_DARK} from 'ui-library/Colors';

const DETAILED_VIEW_ADDITIONAL_VMARGIN = 7;

interface HeightOptions {
  // Overlap is the number of layers of the given component within a slice
  overlap: number;
}

interface LayoutAttributes {
  vMargin: number;
  height: number;
  type?: Type | null;
  border?: number;
}

interface Layout {
  get<K extends keyof LayoutAttributes>(key: K): LayoutAttributes[K];
  set<K extends keyof LayoutAttributes>(
    attributeName: K,
    value: LayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Layout;
  set(obj: Partial<LayoutAttributes>): Layout;
}

/**
 * A Layout is a model for holding the rendering constants and logic for a given component in a slice. Its
 * primary role is to help a view calculate how tall its elements will be via the `height` and `boxHeight`
 * methods, so that its SliceView can place those elements correctly. There is usually one instance of each
 * layout per slice, which is created using the default exported method in this file.
 */
abstract class Layout extends Backbone.Model {
  vMargin(): number {
    return this.get('vMargin');
  }

  height(options?: HeightOptions): number {
    return this.get('height');
  }

  abstract boxHeight(options?: HeightOptions): number;
}

interface DNAOverlayAttributes extends LayoutAttributes {
  single: DNA;
  DNA: DNA;
}

export interface LayeredLayout extends Layout {
  singleHeight(options?: unknown): number;
}

interface DNAOverlay {
  get<K extends keyof DNAOverlayAttributes>(key: K): DNAOverlayAttributes[K];
  set<K extends keyof DNAOverlayAttributes>(
    attributeName: K,
    value: DNAOverlayAttributes[K],
    options?: Backbone.ModelSetOptions
  ): DNAOverlay;
  set(obj: Partial<DNAOverlayAttributes>): DNAOverlay;
}

class DNAOverlay extends Layout {
  override defaults(): Partial<DNAOverlayAttributes> {
    return {
      vMargin: 0,
      single: new DNA(),
    };
  }

  boxHeight(): number {
    return 0;
  }

  override height(): number {
    if (this.get('DNA').get('complement')) {
      return 2 * this.get('DNA').height() + 2 * this.get('DNA').get('spacer');
    } else {
      return this.get('DNA').height() + this.get('DNA').get('spacer');
    }
  }

  singleHeight(): number {
    return this.height();
  }
}

class PrimerAssemblyMismatch extends DNAOverlay {}

class GoldenGateCutSite extends DNAOverlay {}

interface AxisAttributes extends LayoutAttributes {
  tickSize: [number, number, number];
  type: Type;
}

interface Axis {
  get<K extends keyof AxisAttributes>(key: K): AxisAttributes[K];
  set<K extends keyof AxisAttributes>(
    attributeName: K,
    value: AxisAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Axis;
  set(obj: Partial<AxisAttributes>): Axis;
}

class Axis extends Layout {
  override defaults(): Partial<AxisAttributes> {
    return {
      tickSize: [8, 0, 0],
      type: new Type(12),
      vMargin: MapsConstants.AXIS_VMARGIN,
    };
  }

  override height(): number {
    return this.get('type').height() + this.get('tickSize')[0] + 2;
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

class AssemblyConstructAxis extends Axis {
  override height(): number {
    return super.height() + MapsConstants.ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT;
  }
}

interface TitleAttributes extends LayoutAttributes {
  type: Type;
}

interface Title {
  get<K extends keyof TitleAttributes>(key: K): TitleAttributes[K];
  set<K extends keyof TitleAttributes>(
    attributeName: K,
    value: TitleAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Title;
  set(obj: Partial<TitleAttributes>): Title;
}

class Title extends Layout {
  override defaults(): Partial<LayoutAttributes> {
    return {
      type: new Type(20),
      vMargin: 2,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

interface EmptyPlaceholderAttributes extends LayoutAttributes {
  type: Type;
}

interface EmptyPlaceholder {
  get<K extends keyof EmptyPlaceholderAttributes>(key: K): EmptyPlaceholderAttributes[K];
  set<K extends keyof EmptyPlaceholderAttributes>(
    attributeName: K,
    value: EmptyPlaceholderAttributes[K],
    options?: Backbone.ModelSetOptions
  ): EmptyPlaceholder;
  set(obj: Partial<EmptyPlaceholderAttributes>): EmptyPlaceholder;
}

class EmptyPlaceholder extends Layout {
  override defaults(): Partial<LayoutAttributes> {
    return {
      type: new Type(15),
      vMargin: 0,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

// The actual translations view has been ported to React, but Translation layouts are still used by
// ORFs and reading frames.
interface TranslationAttributes extends LayoutAttributes {
  visible: boolean;
  type: Type;
}

interface Translation {
  get<K extends keyof TranslationAttributes>(key: K): TranslationAttributes[K];
  set<K extends keyof TranslationAttributes>(
    attributeName: K,
    value: TranslationAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Translation;
  set(obj: Partial<TranslationAttributes>): Translation;
}

class Translation extends Layout {
  override defaults(): Partial<TranslationAttributes> {
    return {
      vMargin: 5,
      visible: true,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

interface TranslationsAttributes extends LayoutAttributes {
  single: Translation;
  visible: boolean;
  colorScheme: AaSequenceColorScheme;
  indexType: Type;
}

interface Translations {
  get<K extends keyof TranslationsAttributes>(key: K): TranslationsAttributes[K];
  set<K extends keyof TranslationsAttributes>(
    attributeName: K,
    value: TranslationsAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Translations;
  set(obj: Partial<TranslationsAttributes>): Translations;
}

class Translations extends Layout {
  override defaults(): Partial<TranslationsAttributes> {
    return {
      vMargin: 5,
      visible: true,
      colorScheme: AaSequenceColorScheme.RASMOL,
    };
  }

  override height(options: HeightOptions): number {
    return options.overlap * this.get('single').boxHeight();
  }

  singleHeight(): number {
    return this.get('single').height();
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin();
    } else {
      return 0;
    }
  }
}

interface CutSiteAttributes extends LayoutAttributes {
  type: Type;
}

interface CutSite {
  get<K extends keyof CutSiteAttributes>(key: K): CutSiteAttributes[K];
  set<K extends keyof CutSiteAttributes>(
    attributeName: K,
    value: CutSiteAttributes[K],
    options?: Backbone.ModelSetOptions
  ): CutSite;
  set(obj: Partial<CutSiteAttributes>): CutSite;
}

class CutSite extends Layout {
  override defaults(): Partial<LayoutAttributes> {
    return {
      type: new Type(14),
      vMargin: 2,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

interface CutSitesAttributes extends LayoutAttributes {
  cutSite: CutSite;
}

interface CutSites {
  get<K extends keyof CutSitesAttributes>(key: K): CutSitesAttributes[K];
  set<K extends keyof CutSitesAttributes>(
    attributeName: K,
    value: CutSitesAttributes[K],
    options?: Backbone.ModelSetOptions
  ): CutSites;
  set(obj: Partial<CutSitesAttributes>): CutSites;
}

class CutSites extends Layout {
  override defaults(): Partial<CutSitesAttributes> {
    return {
      cutSite: new CutSite(),
      vMargin: 10,
    };
  }

  override height(options: HeightOptions): number {
    return options.overlap * this.get('cutSite').boxHeight();
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin();
    } else {
      return 0;
    }
  }
}

interface PAMSitesAttributes extends LayoutAttributes {
  analysis: CRISPRAnalysis | null;
  // Space between rows
  rowVMargin: number;
  // Minimum horizontal space between adjacent pam sites
  hMargin: number;
  // Minimum height of the line that extends from below the pam site text to the DNA
  lineHeight: number;
  // Padding on rectangle containing score
  scorePadding: number;
  loadingColor: string;
  grayColor: string;
  scoreToColor: d3.ScaleLinear<string, string>;
  type: Type;
}

interface PAMSites {
  get<K extends keyof PAMSitesAttributes>(key: K): PAMSitesAttributes[K];
  set<K extends keyof PAMSitesAttributes>(
    attributeName: K,
    value: PAMSitesAttributes[K],
    options?: Backbone.ModelSetOptions
  ): PAMSites;
  set(obj: Partial<PAMSitesAttributes>): PAMSites;
}

class PAMSites extends Layout {
  override defaults(): Partial<PAMSitesAttributes> {
    return {
      type: new Type(14),
      vMargin: 5,
      analysis: null,
      rowVMargin: 2,
      hMargin: 2,
      lineHeight: 5,
      scorePadding: 2,
      loadingColor: '#FEFEFE',
      grayColor: '#C8C8C8',
      scoreToColor: d3.scaleLinear<string>().domain([0, 50, 100]).range(['#C23B22', '#FFB347', '#77DD77']),
    };
  }

  pamSiteOnTargetMin(): number | undefined {
    return this.get('analysis')?.pamSiteOnTargetMin;
  }

  pamSiteOffTargetMin(): number | undefined {
    return this.get('analysis')?.pamSiteOffTargetMin;
  }

  textOffset(): number {
    return this.get('type').width(1) + this.get('scorePadding');
  }

  leftPamTextOffset(): number {
    return this.get('type').width(2) + this.get('scorePadding') / 2;
  }

  scoreWidth(): number {
    return this.get('type').width(2) + 2 * this.get('scorePadding');
  }

  leftPamBoxWidth(): number {
    return this.get('type').width(4) + this.get('scorePadding');
  }

  override height(options: HeightOptions): number {
    return options.overlap * (this.get('type').height() + this.get('rowVMargin'));
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin() + this.get('lineHeight');
    } else {
      return 0;
    }
  }
}

interface DNAAttributes extends LayoutAttributes {
  complement: boolean;
  spacer: number;
  visible: boolean;
  detailedView: boolean;
  type: Type;
}

interface DNA {
  get<K extends keyof DNAAttributes>(key: K): DNAAttributes[K];
  set<K extends keyof DNAAttributes>(
    attributeName: K,
    value: DNAAttributes[K],
    options?: Backbone.ModelSetOptions
  ): DNA;
  set(obj: Partial<DNAAttributes>): DNA;
}

class DNA extends Layout {
  override defaults(): Partial<DNAAttributes> {
    return {
      complement: true,
      spacer: 4,
      type: new Type(14, 0),
      vMargin: 0,
      visible: true,
      detailedView: false,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  innerHeight(): number {
    if (this.get('complement')) {
      return 2 * this.height() + this.get('spacer');
    } else {
      return this.height();
    }
  }

  boxHeight(): number {
    if (!this.get('visible')) {
      return 0;
    } else if (this.get('detailedView')) {
      // Increases the margin between the DNA slice component and subsequent components in the slice.
      return this.innerHeight() + this.vMargin() + DETAILED_VIEW_ADDITIONAL_VMARGIN;
    } else {
      return this.innerHeight() + this.vMargin();
    }
  }
}

interface AminoAcidsAttributes extends LayoutAttributes {
  spacer: number;
  visible: boolean;
  type: Type;
}

interface AminoAcids {
  get<K extends keyof AminoAcidsAttributes>(key: K): AminoAcidsAttributes[K];
  set<K extends keyof AminoAcidsAttributes>(
    attributeName: K,
    value: AminoAcidsAttributes[K],
    options?: Backbone.ModelSetOptions
  ): AminoAcids;
  set(obj: Partial<AminoAcidsAttributes>): AminoAcids;
}

class AminoAcids extends Layout {
  override defaults(): Partial<AminoAcidsAttributes> {
    const layout = getDefaultAaSequenceLayout().aminoAcids;
    return {
      spacer: layout.spacer,
      type: new Type(layout.type.fontSize, layout.type.letterSpacing),
      vMargin: layout.vMargin,
      visible: layout.visible,
    };
  }

  override height(): number {
    return this.get('type').height();
  }

  innerHeight(): number {
    return this.height();
  }

  boxHeight(): number {
    if (!this.get('visible')) {
      return 0;
    } else {
      return this.innerHeight() + this.vMargin();
    }
  }
}

interface TraceAttributes extends LayoutAttributes {
  visible: boolean;
  height: number;
  qualityFillOpacity: number;
  qualityStrokeOpacity: number;
  qualityFillColor: string;
  qualityStrokeColor: string;
  colorStrokeWidth: number;
  colorMap: {A: string; C: string; G: string; T: string};
  maxQuality: number;
}

interface Trace {
  get<K extends keyof TraceAttributes>(key: K): TraceAttributes[K];
  set<K extends keyof TraceAttributes>(
    attributeName: K,
    value: TraceAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Trace;
  set(obj: Partial<TraceAttributes>): Trace;
}

class Trace extends Layout {
  override defaults(): Partial<TraceAttributes> {
    return {
      vMargin: 0,
      visible: true,
      height: 100,
      qualityFillOpacity: 0.05,
      qualityStrokeOpacity: 0.03,
      qualityFillColor: 'blue',
      qualityStrokeColor: 'black',
      colorStrokeWidth: 1,
      colorMap: {A: 'green', C: 'blue', G: 'black', T: 'red'},
      maxQuality: 75, // arbitrary...
    };
  }

  boxHeight(): number {
    if (this.get('visible')) {
      return this.height();
    } else {
      return 0;
    }
  }
}

interface LineAttributes extends LayoutAttributes {
  maxVMargin: number;
  minVMargin: number;
  maxHeight: number;
  minHeight: number;
  visible: boolean;
  correctFillColor: string;
  mutationFillColor: string;
  unknownValueFillColor: string;
  fillOpacity: number;
  behind: boolean;
}

interface Line {
  get<K extends keyof LineAttributes>(key: K): LineAttributes[K];
  set<K extends keyof LineAttributes>(
    attributeName: K,
    value: LineAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Line;
  set(obj: Partial<LineAttributes>): Line;
}

class Line extends Layout {
  override defaults(): Partial<LineAttributes> {
    return {
      vMargin: 5,
      maxVMargin: 5,
      minVMargin: 1,
      height: 20,
      maxHeight: 20,
      minHeight: 3,
      visible: true,
      correctFillColor: GRAY,
      mutationFillColor: RED_DARK,
      unknownValueFillColor: ORANGE,
      fillOpacity: 1,
      behind: false,
    };
  }

  override height(): number {
    return Math.max(Math.min(this.get('height'), this.get('maxHeight')), this.get('minHeight'));
  }

  override vMargin(): number {
    return Math.max(Math.min(this.get('vMargin'), this.get('maxVMargin')), this.get('minVMargin'));
  }

  setBehind(): void {
    this.set({correctFillColor: 'white', fillOpacity: 0.3, behind: true, vMargin: 0});
  }

  boxHeight(): number {
    if (this.get('behind')) {
      return 0;
    } else if (this.get('visible')) {
      return this.height() + this.vMargin();
    } else {
      return 0;
    }
  }
}

interface HighlightAttributes extends LayoutAttributes {
  visible: boolean;
  correctFillColor: string;
  mutationFillColor: string;
  fillOpacity: number;
  behind: boolean;
}

interface Highlight {
  get<K extends keyof HighlightAttributes>(key: K): HighlightAttributes[K];
  set<K extends keyof HighlightAttributes>(
    attributeName: K,
    value: HighlightAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Highlight;
  set(obj: Partial<HighlightAttributes>): Highlight;
}

class Highlight extends Layout {
  override defaults(): Partial<HighlightAttributes> {
    return {
      vMargin: 0,
      height: 20,
      visible: true,
      correctFillColor: 'green',
      mutationFillColor: 'white',
      fillOpacity: 0.3,
      behind: true,
    };
  }

  boxHeight(): number {
    return 0;
  }
}

interface AnnotationAttributes extends LayoutAttributes {
  border: number;
}

interface Annotation {
  get<K extends keyof AnnotationAttributes>(key: K): AnnotationAttributes[K];
  set<K extends keyof AnnotationAttributes>(
    attributeName: K,
    value: AnnotationAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Annotation;
  set(obj: Partial<AnnotationAttributes>): Annotation;
}

class Annotation extends Layout {
  override defaults(): Partial<LayoutAttributes> {
    return {
      border: 1,
      height: 12,
      vMargin: 2,
    };
  }

  boxHeight(): number {
    return 2 * this.get('border') + this.vMargin() + this.height();
  }
}

interface AnnotationsAttributes extends LayoutAttributes {
  single: Annotation;
  type: Type;
}

interface Annotations {
  get<K extends keyof AnnotationsAttributes>(key: K): AnnotationsAttributes[K];
  set<K extends keyof AnnotationsAttributes>(
    attributeName: K,
    value: AnnotationsAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Annotations;
  set(obj: Partial<AnnotationsAttributes>): Annotations;
}

class Annotations extends Layout {
  override defaults(): Partial<AnnotationsAttributes> {
    return {
      single: new Annotation(),
      type: new Type(13),
      vMargin: 10,
    };
  }

  override height(options: HeightOptions): number {
    return options.overlap * this.get('single').boxHeight();
  }

  singleHeight(): number {
    return this.get('single').height();
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin();
    } else {
      return 0;
    }
  }
}

interface ORFAttributes extends LayoutAttributes {
  lineHeight: number;
  startColor: string;
  endColor: string;
  border: number;
}

interface ORF {
  get<K extends keyof ORFAttributes>(key: K): ORFAttributes[K];
  set<K extends keyof ORFAttributes>(
    attributeName: K,
    value: ORFAttributes[K],
    options?: Backbone.ModelSetOptions
  ): ORF;
  set(obj: Partial<ORFAttributes>): ORF;
}

class ORF extends Layout {
  override defaults(): Partial<ORFAttributes> {
    return {
      border: 1,
      height: 10,
      lineHeight: 1,
      vMargin: 1,
      startColor: 'green',
      endColor: 'yellow',
    };
  }

  boxHeight(): number {
    return 2 * this.get('border') + this.vMargin() + this.height();
  }
}

interface ORFsAttributes extends LayoutAttributes {
  single: ORF;
}

interface ORFs {
  get<K extends keyof ORFsAttributes>(key: K): ORFsAttributes[K];
  set<K extends keyof ORFsAttributes>(
    attributeName: K,
    value: ORFsAttributes[K],
    options?: Backbone.ModelSetOptions
  ): ORFs;
  set(obj: Partial<ORFsAttributes>): ORFs;
}

class ORFs extends Layout {
  override defaults(): Partial<ORFsAttributes> {
    return {
      single: new ORF(),
      type: new Type(5),
      vMargin: 10,
    };
  }

  override height(options: HeightOptions): number {
    return options.overlap * this.get('single').boxHeight();
  }

  singleHeight(): number {
    return this.get('single').height();
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin();
    } else {
      return 0;
    }
  }
}

interface AlignmentCommentAttributes extends LayoutAttributes {
  border: number;
}

interface AlignmentComment {
  get<K extends keyof AlignmentCommentAttributes>(key: K): AlignmentCommentAttributes[K];
  set<K extends keyof AlignmentCommentAttributes>(
    attributeName: K,
    value: AlignmentCommentAttributes[K],
    options?: Backbone.ModelSetOptions
  ): AlignmentComment;
  set(obj: Partial<AlignmentCommentAttributes>): AlignmentComment;
}

class AlignmentComment extends Layout {
  override defaults(): Partial<LayoutAttributes> {
    return {
      border: 1,
      height: 12,
      vMargin: 2,
    };
  }

  boxHeight(): number {
    return 2 * this.get('border') + this.vMargin() + this.height();
  }
}

interface AlignmentCommentsAttributes extends LayoutAttributes {
  single: AlignmentComment;
}

interface AlignmentComments {
  get<K extends keyof AlignmentCommentsAttributes>(key: K): AlignmentCommentsAttributes[K];
  set<K extends keyof AlignmentCommentsAttributes>(
    attributeName: K,
    value: AlignmentCommentsAttributes[K],
    options?: Backbone.ModelSetOptions
  ): AlignmentComments;
  set(obj: Partial<AlignmentCommentsAttributes>): AlignmentComments;
}

class AlignmentComments extends Layout {
  override defaults(): Partial<AlignmentCommentsAttributes> {
    return {
      single: new AlignmentComment(),
      type: new Type(13),
      vMargin: 10,
    };
  }

  override height(options: HeightOptions): number {
    return options.overlap * this.get('single').boxHeight();
  }

  singleHeight(): number {
    return this.get('single').height();
  }

  boxHeight(options: HeightOptions): number {
    if (options.overlap > 0) {
      return this.height(options) + this.vMargin();
    } else {
      return 0;
    }
  }
}

export interface LinearLayout {
  Axis: Axis;
  AssemblyConstructAxis: AssemblyConstructAxis;
  ORFs: ORFs;
  DNA: DNA;
  AminoAcids: AminoAcids;
  Translations: Translations;
  AlignmentComments: AlignmentComments;
  Trace: Trace;
  Annotations: Annotations;
  CutSites: CutSites;
  PAMSites: PAMSites;
  Line: Line;
  Highlight: Highlight;
  Title: Title;
  PrimerAssemblyMismatch: PrimerAssemblyMismatch;
  GoldenGateCutSite: GoldenGateCutSite;
  EmptyPlaceholder: EmptyPlaceholder;
}

export default function (): LinearLayout {
  const dna = new DNA();
  return {
    Axis: new Axis(),
    AssemblyConstructAxis: new AssemblyConstructAxis(),
    DNA: dna,
    AminoAcids: new AminoAcids(),
    ORFs: new ORFs(),
    AlignmentComments: new AlignmentComments(),
    Trace: new Trace(),
    Annotations: new Annotations(),
    CutSites: new CutSites(),
    PAMSites: new PAMSites(),
    Translations: new Translations({
      single: new Translation({type: dna.get('type')}),
      indexType: dna.get('type'),
    }),
    Line: new Line(),
    Highlight: new Highlight(),
    Title: new Title(),
    PrimerAssemblyMismatch: new PrimerAssemblyMismatch({DNA: dna}),
    GoldenGateCutSite: new GoldenGateCutSite({DNA: dna}),
    EmptyPlaceholder: new EmptyPlaceholder(),
  };
}

export {
  Layout,
  AlignmentComments,
  AminoAcids,
  Annotations,
  Axis,
  DNA,
  EmptyPlaceholder,
  GoldenGateCutSite,
  Line,
  ORFs,
  PAMSites,
  PrimerAssemblyMismatch,
  Title,
  Translations,
  CutSites,
  Trace,
};
